import { EventEmitterInterface } from 'pf-frontend-common/src/module/event/emitter.interface';

import { CountryCodeEnum } from 'common/data/country/code.enum';
import { UserRegisterModel } from 'common/data/user/register/model';
import { configGetCountry } from 'common/helper/config/get-country';
import { i18nTranslate } from 'common/helper/i18n/translate';
import { DataStore } from 'common/module/data/store';
import { GoogleRecaptchaViewStore } from 'common/module/google/recaptcha/view-store';
import { GoogleRecaptchaViewStoreEvent } from 'common/module/google/recaptcha/view-store.event';
import { InputValidatorInterface } from 'common/module/input/validator.interface';
import { InputValidatorEmail } from 'common/module/input/validator/email';
import { InputValidatorRequired } from 'common/module/input/validator/required';
import { StatechartStoreInterface } from 'common/module/statechart/store.interface';
import { UserAuthenticationEvent } from 'common/module/user/authentication.event';
import { UserRegistrationFieldErrorsInterface } from 'common/module/user/registration/field-errors.interface';
import { UserRegistrationFieldsInterface } from 'common/module/user/registration/fields.interface';
import { UserRegistrationStatechart } from 'common/module/user/registration/statechart';
import { UserRegistrationStatechartEvent } from 'common/module/user/registration/statechart.event';
import { UserRegistrationStatechartActionEnum } from 'common/module/user/registration/statechart/action.enum';
import { UserRegistrationTemplatePropsInterface } from 'common/module/user/registration/template-props.interface';
import { UserRegistrationViewStoreEvent } from 'common/module/user/registration/view-store.event';
import { UserViewStoreInterface } from 'common/module/user/view-store.interface';
import { UserAuthenticationServiceInterface } from 'common/service/user-authentication/service.interface';

export class UserRegistrationViewStore extends DataStore implements UserViewStoreInterface {
  /**
   * @inheritDoc
   */
  protected state: UserRegistrationTemplatePropsInterface;

  /**
   * Constructor
   */
  constructor(
    eventEmitter: EventEmitterInterface,
    private userAuthenticationService: UserAuthenticationServiceInterface,
    private captcha: GoogleRecaptchaViewStore,
    private statechartStore: StatechartStoreInterface
  ) {
    super(eventEmitter);
  }

  /**
   * @inheritDoc
   */
  public initialize(): void {
    this.state = {
      error: '',
      fields: new UserRegisterModel(),
      fieldErrors: {
        userName: '',
        email: '',
        password: '',
        captcha_token: '',
      },
      loaderEnabled: false,
      isAdditionalConditionEnabled: configGetCountry().code === CountryCodeEnum.ma,
      isPasswordVisible: false,
      setIsPasswordVisible: this.setIsPasswordVisible,
      captcha: this.captcha.getState(),
      onChangeInput: this.onChangeInput,
      onSubmit: this.onSubmit,
      onClickLogin: this.onClickLogin,
      onClickTermsAndConditions: this.onClickTermsAndConditions,
      onClickUseEmail: this.onClickUseEmail,
      onClickOptedIn: this.onClickOptedIn,
      onClose: this.onClose,
      onReset: this.onReset,
    };

    this.statechartStore.initialize({
      statechart: UserRegistrationStatechart,
    });

    // Attach observers
    this.observers();
  }

  /**
   * @inheritDoc
   */
  public updateLoaderEnabled(enabled: boolean): void {
    this.setState(
      {
        ...this.state,
        loaderEnabled: enabled,
      },
      this.updateLoaderEnabled
    );
  }

  /**
   * Reset state
   */
  public reset(): void {
    const userRegistrationFields = new UserRegisterModel();
    userRegistrationFields.last_name = this.state.fields.last_name;
    userRegistrationFields.first_name = this.state.fields.first_name;
    userRegistrationFields.email = this.state.fields.email;
    userRegistrationFields.captcha_token = this.state.fields.captcha_token;

    this.state = {
      ...this.state,
      error: '',
      fields: userRegistrationFields,
      fieldErrors: {
        userName: '',
        email: '',
        password: '',
        captcha_token: '',
      },
    };

    this.setState(this.state);

    this.statechartStore.transit({
      event: UserRegistrationStatechartEvent.reset,
      payload: null,
    });
  }

  /**
   * Form validators
   */
  protected getValidators(): {
    [key: string]: InputValidatorInterface[];
  } {
    const validations = {
      email: [
        new InputValidatorRequired(i18nTranslate('Enter your email')),
        new InputValidatorEmail(i18nTranslate('Please enter a valid email')),
      ],
      password: [new InputValidatorRequired(i18nTranslate('Please enter a password'))],
    };

    return configGetCountry().code === CountryCodeEnum.ae
      ? {
          ...validations,
          captcha_token: [],
        }
      : {
          ...validations,
          userName: [new InputValidatorRequired(i18nTranslate('Please enter a valid first and last name'))],
          captcha_token: [],
        };
  }

  /**
   * @inheritDoc
   */
  private observers(): void {
    this.userAuthenticationService
      .getEventEmitter()
      .addListener(UserAuthenticationEvent.registrationFailed, this.onRegistrationFailed);

    this.userAuthenticationService
      .getEventEmitter()
      .addListener(UserAuthenticationEvent.registrationValidationFailed, this.onRegistrationValidationFailed);

    this.captcha.getEventEmitter().addListener(GoogleRecaptchaViewStoreEvent.change, this.onChangeCaptcha);

    this.statechartStore
      .getEventEmitter()
      .addListener(UserRegistrationStatechartActionEnum.showLoader, this.onShowLoaderActionStatechart);

    this.statechartStore
      .getEventEmitter()
      .addListener(UserRegistrationStatechartActionEnum.validateInput, this.onValidateInputActionStatechart);

    this.statechartStore
      .getEventEmitter()
      .addListener(UserRegistrationStatechartActionEnum.executeCaptcha, this.onExecuteCaptchaActionStatechart);

    this.statechartStore
      .getEventEmitter()
      .addListener(UserRegistrationStatechartActionEnum.register, this.onRegisterActionStatechart);

    this.statechartStore
      .getEventEmitter()
      .addListener(UserRegistrationStatechartActionEnum.resetCaptchaToken, this.onResetCaptchaTokenActionStatechart);
  }

  /**
   * Validate form fields
   */
  private validateFields(): boolean {
    let isValid = true;

    const validators = this.getValidators();

    Object.keys(validators).forEach((fieldName: keyof UserRegistrationFieldErrorsInterface) => {
      let errors: string[] = [];

      errors = validators[fieldName]
        .map((validator: InputValidatorInterface) => {
          // Validate both first name and last name
          if (fieldName === 'userName') {
            return (
              validator.getError(this.state.fields.first_name, null) ||
              validator.getError(this.state.fields.last_name, null)
            );
          }

          return validator.getError(this.state.fields[fieldName], null);
        })
        .filter((message: string) => !!message);

      // Only keep first error if any
      this.state.fieldErrors[fieldName] = errors[0] || '';
      isValid = isValid && !this.state.fieldErrors[fieldName];
    });

    this.setState(this.state);

    return isValid;
  }

  /**
   * Change input value
   */
  private onChangeInput = (e: Event, fieldName: keyof PickByValue<UserRegistrationFieldsInterface, string>): void => {
    this.state.fields[fieldName] = (e.target as HTMLInputElement).value;
    this.setState(this.state);
  };

  /**
   * Captcha value has changed
   */
  private onChangeCaptcha = () => {
    this.state.fields.captcha_token = this.captcha.getState().value;
    this.setState(this.state);

    this.statechartStore.transit({
      event: UserRegistrationStatechartEvent.captchaValueChanged,
      payload: null,
    });
  };

  /**
   * Click close button
   */
  private onClose = (): void => {
    this.getEventEmitter().emit(UserRegistrationViewStoreEvent.clickClose);
  };

  /**
   * Submit registration form
   */
  private onSubmit = (e: Event): void => {
    e.preventDefault();

    if (
      this.state.isAdditionalConditionEnabled &&
      (!this.state.fields.useEmail || !this.state.fields.termsAndConditions)
    ) {
      return;
    }

    this.statechartStore.transit({
      event: UserRegistrationStatechartEvent.wantToRegister,
      payload: null,
    });
  };

  /**
   * Reset state
   */
  private onReset = (): void => {
    this.state = {
      ...this.state,
      error: '',
      fields: new UserRegisterModel(),
      fieldErrors: {
        userName: '',
        email: '',
        password: '',
        captcha_token: '',
      },
    };

    this.setState(this.state);
  };

  /**
   * Click login button
   */
  private onClickLogin = (): void => {
    this.getEventEmitter().emit(UserRegistrationViewStoreEvent.clickLogin);
  };

  /**
   * User registration failed
   */
  private onRegistrationFailed = (): void => {
    this.state.error = i18nTranslate('Something went wrong! Please try again later');
    this.setState(this.state);

    this.statechartStore.transit({
      event: UserRegistrationStatechartEvent.registrationFailed,
      payload: null,
    });
  };

  /**
   * User registration validation failed
   */
  private onRegistrationValidationFailed = (errors: UserRegistrationFieldErrorsInterface): void => {
    if (errors.captcha_token.length) {
      this.captcha.reset();
    }

    this.state.fieldErrors = errors;
    this.setState(this.state);

    this.statechartStore.transit({
      event: UserRegistrationStatechartEvent.registrationFailed,
      payload: null,
    });
  };

  /**
   * @inheritDoc
   */
  private onClickTermsAndConditions = () => {
    this.state.fields.termsAndConditions = !this.state.fields.termsAndConditions;
    this.setState(this.state);
  };

  /**
   * @inheritDoc
   */
  private onClickUseEmail = () => {
    this.state.fields.useEmail = !this.state.fields.useEmail;
    this.state.fields.opted_in = !this.state.fields.opted_in;
    this.setState(this.state);
  };

  /**
   * @inheritDoc
   */
  private onClickOptedIn = () => {
    this.state.fields.opted_in = !this.state.fields.opted_in;
    this.setState(this.state);
  };

  /**
   * Show loader
   */
  private onShowLoaderActionStatechart = () => {
    this.updateLoaderEnabled(true);
  };

  /**
   * Validate input
   */
  private onValidateInputActionStatechart = () => {
    const isValid = this.validateFields();

    if (isValid) {
      this.statechartStore.transit({
        event: UserRegistrationStatechartEvent.inputIsValid,
        payload: null,
      });

      return;
    }

    this.statechartStore.transit({
      event: UserRegistrationStatechartEvent.inputIsNotValid,
      payload: null,
    });
  };

  /**
   * Execute captcha
   */
  private onExecuteCaptchaActionStatechart = () => {
    if (this.state.fields.captcha_token) {
      this.statechartStore.transit({
        event: UserRegistrationStatechartEvent.captchaValueChanged,
        payload: null,
      });

      return;
    }

    this.captcha.execute();
  };

  /**
   * Register
   */
  private onRegisterActionStatechart = () => {
    this.userAuthenticationService.register(this.state.fields);
  };

  /**
   * Reset captcha_token field
   */
  private onResetCaptchaTokenActionStatechart = () => {
    this.captcha.reset();
    this.state.fields.captcha_token = '';
    this.setState(this.state);
  };

  /**
   * Set is password visible
   */
  private setIsPasswordVisible = (isVisible: boolean) => {
    this.setState({
      ...this.state,
      isPasswordVisible: isVisible,
    });
  };
}
